import React, { useState, useEffect } from 'react';
import AttachBusinessCardToggle from '../components/businessCard/AttachBusinessCardToggle.jsx';
import { useParams, useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar.jsx';
import BotTrainer from '../components/BotTrainer.jsx';
import { apiGet, apiPost, apiPut } from '../lib/api.js';
import toast from 'react-hot-toast';
import {
  Bot, Save, ArrowLeft, Plus, Trash2, Play, Pause, MessageSquare, Phone, Shield, BookOpen, Target,
  Sparkles, Send, AlertTriangle, User as UserIcon, GraduationCap,
} from 'lucide-react';

const TONES = [
  { v: 'friendly', label: 'Friendly', desc: 'Warm, casual, approachable' },
  { v: 'professional', label: 'Professional', desc: 'Polished, corporate' },
  { v: 'casual', label: 'Casual', desc: 'Conversational, relaxed' },
  { v: 'urgent', label: 'Urgent', desc: 'Time-sensitive, action-oriented' },
  { v: 'empathetic', label: 'Empathetic', desc: 'Caring, understanding' },
];

const VOICES = [
  { v: 'alloy', label: 'Alloy — Neutral' },
  { v: 'echo', label: 'Echo — Warm male' },
  { v: 'shimmer', label: 'Shimmer — Bright female' },
  { v: 'onyx', label: 'Onyx — Deep male' },
  { v: 'nova', label: 'Nova — Friendly female' },
];

const TEMPLATES = {
  fex_concierge: {
    name: 'FEX Concierge',
    persona: { name: 'Civi', role: 'Final Expense Concierge', tone: 'friendly', voiceId: 'shimmer' },
    openingMessage: "Hi #first_name, this is Civi with #company_name — I'm an AI assistant helping #agent_name. Got a quick second?",
    closingMessage: "Thanks #first_name — I've passed everything to #agent_name. Talk soon!",
    systemPrompt: 'You help final-expense leads understand their options. Keep it simple, never quote exact prices, always defer pricing questions to the human agent. Confirm age, state, and existing coverage. Aim to schedule a 15-min call.',
    goals: [
      { label: 'Confirm interest', description: 'Verify they still want quotes', priority: 1, successCriteria: 'Lead confirms interest' },
      { label: 'Qualify basics', description: 'Get age range and state', priority: 2, successCriteria: 'Age + state captured' },
      { label: 'Book a call', description: 'Schedule a 15-min review with the agent', priority: 3, successCriteria: 'Calendar slot confirmed' },
    ],
    knowledge: [
      { title: 'About Final Expense', content: 'Final expense is whole life insurance designed to cover funeral costs and final bills. Typical coverage $5K–$25K. No medical exam in most cases.' },
      { title: 'How quotes work', content: 'Quotes vary by age, state, tobacco use, and coverage amount. The agent provides exact pricing on the call.' },
    ],
  },
  reengage: {
    name: 'Re-engage Bot',
    persona: { name: 'Civi', role: 'Re-engagement Specialist', tone: 'casual', voiceId: 'alloy' },
    openingMessage: "Hey #first_name, just checking in — are you still looking at coverage options?",
    systemPrompt: 'Re-engage cold leads who haven\'t responded in 14+ days. Be light, low-pressure. One question at a time. If they say no thanks, gracefully exit.',
    goals: [{ label: 'Re-spark interest', description: 'Get them talking again', priority: 1, successCriteria: 'They reply with anything other than STOP' }],
  },
  outbound_voice: {
    name: 'Outbound Voice',
    persona: { name: 'Civi', role: 'Outbound Voice Agent', tone: 'professional', voiceId: 'nova' },
    openingMessage: "Hi #first_name, this is Civi with #company_name. I'm an AI assistant — is now a good time for a quick 60-second check-in?",
    systemPrompt: 'Voice-first outbound. Short sentences. Pause after questions. If they sound busy, offer to text instead. Always disclose you are an AI.',
    goals: [{ label: 'Confirm interest', description: 'Verify they want a callback', priority: 1, successCriteria: 'Yes/no captured' }],
  },
};

function emptyBot() {
  return {
    name: 'New AI Bot',
    type: 'hybrid',
    status: 'draft',
    persona: { name: 'Civi', role: 'Insurance Assistant', tone: 'friendly', voiceId: 'alloy', language: 'en-US' },
    goals: [],
    systemPrompt: '',
    openingMessage: '',
    attachDigitalBusinessCard: true,
    closingMessage: '',
    knowledge: [],
    guardrails: {
      maxMessagesPerLead: 8, maxCallAttempts: 3,
      stopKeywords: ['stop', 'unsubscribe', 'remove', 'quit'],
      handoffKeywords: ['agent', 'human', 'representative'],
      forbiddenTopics: ['medical advice', 'legal advice', 'financial advice'],
      requireDisclosure: true, quietHoursStart: '21:00', quietHoursEnd: '08:00', respectTimezone: true,
    },
    outbound: { enabled: true, fromNumberPool: [], cadence: 'smart', aiModel: 'gpt-4o-mini', voiceModel: 'gpt-4o-realtime-preview' },
    triggers: { onNewLead: false, onMissedCall: false, onInboundReply: true, onWarmDisposition: false },
    stats: { messagesSent: 0, callsMade: 0, leadsEngaged: 0, bookings: 0, handoffs: 0, optOuts: 0 },
  };
}

export default function AIBotStudio() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id || id === 'new';
  const [bot, setBot] = useState(emptyBot());
  const [tab, setTab] = useState('persona');
  const [saving, setSaving] = useState(false);

  // Test console
  const [testMsg, setTestMsg] = useState('Hi, I got your text — what is this about?');
  const [transcript, setTranscript] = useState([]);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!isNew) {
      apiGet(`/aibots/${id}`).then((b) => b && setBot({ ...emptyBot(), ...b })).catch(() => {});
    }
  }, [id, isNew]);

  function applyTemplate(key) {
    const t = TEMPLATES[key];
    if (!t) return;
    setBot({ ...emptyBot(), ...t });
    toast.success(`Loaded template: ${t.name}`);
  }

  async function save() {
    setSaving(true);
    try {
      if (isNew) {
        const r = await apiPost('/aibots', bot);
        toast.success('Bot saved');
        nav(`/aibots/${r._id || 'new'}`, { replace: true });
      } else {
        await apiPut(`/aibots/${id}`, bot);
        toast.success('Bot saved');
      }
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function runTest() {
    if (!testMsg.trim()) return;
    setTesting(true);
    const userTurn = { role: 'user', content: testMsg, at: new Date().toISOString() };
    setTranscript((t) => [...t, userTurn]);
    setTestMsg('');
    try {
      // In demo mode, generate a contextual reply locally based on bot config
      const reply = synthesizeDemoReply(bot, testMsg);
      await new Promise((r) => setTimeout(r, 600));
      setTranscript((t) => [...t, { role: 'bot', content: reply, at: new Date().toISOString() }]);
    } catch (e) {
      setTranscript((t) => [...t, { role: 'bot', content: '[error] ' + e.message, at: new Date().toISOString() }]);
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="space-y-4">
      <TopBar title={bot.name} subtitle="AI Bot Studio" />

      <div className="panel p-4 flex items-center gap-3">
        <button onClick={() => nav('/aibots')} className="btn-ghost flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
        <input className="input flex-1 text-lg font-semibold" value={bot.name} onChange={(e) => setBot({ ...bot, name: e.target.value })} />
        <select className="input w-32" value={bot.type} onChange={(e) => setBot({ ...bot, type: e.target.value })}>
          <option value="sms">SMS only</option>
          <option value="voice">Voice only</option>
          <option value="hybrid">Hybrid</option>
        </select>
        <span className={`text-xs px-2 py-1 rounded-full ${bot.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{bot.status}</span>
        <button onClick={() => setBot({ ...bot, status: bot.status === 'active' ? 'paused' : 'active' })} className="btn-ghost flex items-center gap-1">
          {bot.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2"><Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}</button>
      </div>

      {isNew && (
        <div className="panel p-4">
          <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-civix-purple" /> Start from a template</div>
          <div className="grid md:grid-cols-3 gap-2">
            <button onClick={() => applyTemplate('fex_concierge')} className="panel p-3 text-left hover:border-civix-purple">
              <div className="font-semibold text-sm">FEX Concierge</div>
              <div className="text-xs text-white/60 mt-1">Friendly inbound qualifier — books quote calls</div>
            </button>
            <button onClick={() => applyTemplate('reengage')} className="panel p-3 text-left hover:border-civix-purple">
              <div className="font-semibold text-sm">Re-engage Bot</div>
              <div className="text-xs text-white/60 mt-1">Light-touch outreach to cold leads</div>
            </button>
            <button onClick={() => applyTemplate('outbound_voice')} className="panel p-3 text-left hover:border-civix-purple">
              <div className="font-semibold text-sm">Outbound Voice</div>
              <div className="text-xs text-white/60 mt-1">AI calls leads — qualifies + transfers to human</div>
            </button>
          </div>
        </div>
      )}

      <div className="panel p-1 flex flex-wrap">
        {[
          { v: 'persona', label: 'Persona', icon: UserIcon },
          { v: 'goals', label: 'Goals', icon: Target },
          { v: 'knowledge', label: 'Knowledge', icon: BookOpen },
          { v: 'train', label: 'Train', icon: GraduationCap },
          { v: 'guardrails', label: 'Guardrails', icon: Shield },
          { v: 'channels', label: 'Channels', icon: MessageSquare },
          { v: 'triggers', label: 'Triggers', icon: Sparkles },
          { v: 'test', label: 'Test Console', icon: Send },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.v} onClick={() => setTab(t.v)} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm ${tab === t.v ? 'bg-civix-purple/20 text-white' : 'text-white/60 hover:text-white'}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'persona' && (
        <div className="panel p-6 max-w-3xl space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Bot display name</label>
              <input className="input" value={bot.persona.name} onChange={(e) => setBot({ ...bot, persona: { ...bot.persona, name: e.target.value } })} />
            </div>
            <div>
              <label className="label">Role</label>
              <input className="input" value={bot.persona.role} onChange={(e) => setBot({ ...bot, persona: { ...bot.persona, role: e.target.value } })} />
            </div>
          </div>
          <div>
            <label className="label">Tone</label>
            <div className="grid grid-cols-5 gap-2">
              {TONES.map((t) => (
                <button key={t.v} onClick={() => setBot({ ...bot, persona: { ...bot.persona, tone: t.v } })}
                  className={`panel p-3 text-left ${bot.persona.tone === t.v ? 'border-civix-purple bg-civix-purple/10' : 'border-civix-border'}`}>
                  <div className="font-semibold text-sm">{t.label}</div>
                  <div className="text-xs text-white/60 mt-1">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Voice (for AI calls)</label>
              <select className="input" value={bot.persona.voiceId} onChange={(e) => setBot({ ...bot, persona: { ...bot.persona, voiceId: e.target.value } })}>
                {VOICES.map((v) => <option key={v.v} value={v.v}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Language</label>
              <select className="input" value={bot.persona.language} onChange={(e) => setBot({ ...bot, persona: { ...bot.persona, language: e.target.value } })}>
                <option value="en-US">English (US)</option>
                <option value="es-US">Spanish (US)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">Opening message</label>
            <textarea className="input min-h-[60px]" value={bot.openingMessage} onChange={(e) => setBot({ ...bot, openingMessage: e.target.value })} placeholder="Hi #first_name, this is Civi…" />
            <AttachBusinessCardToggle
              className="mt-2"
              id="bot-attach-business-card"
              checked={bot.attachDigitalBusinessCard}
              onChange={(v) => setBot({ ...bot, attachDigitalBusinessCard: v })}
            />
          </div>
          <div>
            <label className="label">System prompt (instructions to the AI)</label>
            <textarea className="input min-h-[120px] font-mono text-sm" value={bot.systemPrompt} onChange={(e) => setBot({ ...bot, systemPrompt: e.target.value })} placeholder="You are an insurance assistant. Keep replies under 320 chars. Never quote exact prices. Always offer to book a call." />
          </div>
          <div>
            <label className="label">Closing message</label>
            <textarea className="input min-h-[60px]" value={bot.closingMessage} onChange={(e) => setBot({ ...bot, closingMessage: e.target.value })} />
          </div>
        </div>
      )}

      {tab === 'goals' && (
        <div className="panel p-6 max-w-3xl space-y-3">
          <div className="text-sm text-white/60">Goals tell the bot what success looks like. They're applied in priority order — once goal 1 is hit, the bot moves to goal 2.</div>
          {bot.goals.map((g, i) => (
            <div key={i} className="panel p-4 grid grid-cols-[40px_1fr_40px] gap-3 items-start">
              <div className="text-2xl font-semibold gradient-text">{i + 1}</div>
              <div className="space-y-2">
                <input className="input" placeholder="Goal label — e.g. Book a quote call" value={g.label} onChange={(e) => updateGoal(i, { label: e.target.value })} />
                <textarea className="input" rows="2" placeholder="Description / how the bot should approach this goal" value={g.description || ''} onChange={(e) => updateGoal(i, { description: e.target.value })} />
                <input className="input" placeholder="Success criteria — e.g. Lead picks a calendar time" value={g.successCriteria || ''} onChange={(e) => updateGoal(i, { successCriteria: e.target.value })} />
              </div>
              <button onClick={() => setBot({ ...bot, goals: bot.goals.filter((_, j) => j !== i) })} className="btn-ghost text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => setBot({ ...bot, goals: [...bot.goals, { label: '', description: '', priority: bot.goals.length + 1, successCriteria: '' }] })} className="btn-ghost flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add a goal
          </button>
        </div>
      )}

      {tab === 'knowledge' && (
        <div className="panel p-6 max-w-3xl space-y-3">
          <div className="text-sm text-white/60">Give the bot reference material it can pull from. FAQs, product info, scripts, objection handling.</div>
          {bot.knowledge.map((k, i) => (
            <div key={i} className="panel p-4 grid grid-cols-[1fr_40px] gap-3 items-start">
              <div className="space-y-2">
                <input className="input" placeholder="Title — e.g. About Final Expense" value={k.title} onChange={(e) => updateKnowledge(i, { title: e.target.value })} />
                <textarea className="input min-h-[80px]" placeholder="Content the bot can reference" value={k.content} onChange={(e) => updateKnowledge(i, { content: e.target.value })} />
              </div>
              <button onClick={() => setBot({ ...bot, knowledge: bot.knowledge.filter((_, j) => j !== i) })} className="btn-ghost text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => setBot({ ...bot, knowledge: [...bot.knowledge, { title: '', content: '' }] })} className="btn-ghost flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add knowledge
          </button>
        </div>
      )}

      {tab === 'guardrails' && (
        <div className="panel p-6 max-w-3xl space-y-4">
          <div className="flex items-start gap-2 panel p-3 bg-civix-panelLight">
            <AlertTriangle className="w-4 h-4 text-civix-cyan shrink-0 mt-0.5" />
            <div className="text-sm text-white/70">Guardrails keep your bot safe and compliant. Stop keywords trigger immediate opt-out. Handoff keywords route to a human.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Max messages per lead</label>
              <input type="number" className="input" value={bot.guardrails.maxMessagesPerLead} onChange={(e) => setBot({ ...bot, guardrails: { ...bot.guardrails, maxMessagesPerLead: +e.target.value } })} />
            </div>
            <div>
              <label className="label">Max call attempts</label>
              <input type="number" className="input" value={bot.guardrails.maxCallAttempts} onChange={(e) => setBot({ ...bot, guardrails: { ...bot.guardrails, maxCallAttempts: +e.target.value } })} />
            </div>
            <div>
              <label className="label">Quiet hours start</label>
              <input type="time" className="input" value={bot.guardrails.quietHoursStart} onChange={(e) => setBot({ ...bot, guardrails: { ...bot.guardrails, quietHoursStart: e.target.value } })} />
            </div>
            <div>
              <label className="label">Quiet hours end</label>
              <input type="time" className="input" value={bot.guardrails.quietHoursEnd} onChange={(e) => setBot({ ...bot, guardrails: { ...bot.guardrails, quietHoursEnd: e.target.value } })} />
            </div>
          </div>
          <KeywordList label="Stop keywords (immediate opt-out)" value={bot.guardrails.stopKeywords} onChange={(v) => setBot({ ...bot, guardrails: { ...bot.guardrails, stopKeywords: v } })} />
          <KeywordList label="Handoff keywords (route to human)" value={bot.guardrails.handoffKeywords} onChange={(v) => setBot({ ...bot, guardrails: { ...bot.guardrails, handoffKeywords: v } })} />
          <KeywordList label="Forbidden topics" value={bot.guardrails.forbiddenTopics} onChange={(v) => setBot({ ...bot, guardrails: { ...bot.guardrails, forbiddenTopics: v } })} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={bot.guardrails.requireDisclosure} onChange={(e) => setBot({ ...bot, guardrails: { ...bot.guardrails, requireDisclosure: e.target.checked } })} />
            Require AI disclosure if asked ("Are you a real person?")
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={bot.guardrails.respectTimezone} onChange={(e) => setBot({ ...bot, guardrails: { ...bot.guardrails, respectTimezone: e.target.checked } })} />
            Respect lead's timezone for quiet hours
          </label>
        </div>
      )}

      {tab === 'channels' && (
        <div className="panel p-6 max-w-3xl space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={bot.outbound.enabled} onChange={(e) => setBot({ ...bot, outbound: { ...bot.outbound, enabled: e.target.checked } })} />
            Outbound enabled — bot can initiate contact
          </label>
          <div>
            <label className="label">Send cadence</label>
            <select className="input" value={bot.outbound.cadence} onChange={(e) => setBot({ ...bot, outbound: { ...bot.outbound, cadence: e.target.value } })}>
              <option value="immediate">Immediate — send as triggers fire</option>
              <option value="drip">Drip — paced over time</option>
              <option value="smart">Smart — AI picks optimal send time</option>
            </select>
          </div>
          <div>
            <label className="label">From-number pool (comma separated E.164)</label>
            <input className="input" placeholder="+14068604882, +14065551203" value={bot.outbound.fromNumberPool?.join(', ') || ''} onChange={(e) => setBot({ ...bot, outbound: { ...bot.outbound, fromNumberPool: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">SMS / chat model</label>
              <select className="input" value={bot.outbound.aiModel} onChange={(e) => setBot({ ...bot, outbound: { ...bot.outbound, aiModel: e.target.value } })}>
                <option value="gpt-4o-mini">gpt-4o-mini (fast & cheap)</option>
                <option value="gpt-4o">gpt-4o (highest quality)</option>
                <option value="claude-3-5-sonnet">claude-3-5-sonnet</option>
              </select>
            </div>
            <div>
              <label className="label">Voice model</label>
              <select className="input" value={bot.outbound.voiceModel} onChange={(e) => setBot({ ...bot, outbound: { ...bot.outbound, voiceModel: e.target.value } })}>
                <option value="gpt-4o-realtime-preview">gpt-4o-realtime (low latency)</option>
                <option value="elevenlabs-turbo">ElevenLabs Turbo</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {tab === 'triggers' && (
        <div className="panel p-6 max-w-3xl space-y-3">
          <div className="text-sm text-white/60">When should this bot wake up and engage?</div>
          {[
            { k: 'onNewLead', label: 'On new lead', desc: 'Bot reaches out within minutes of a lead arriving' },
            { k: 'onMissedCall', label: 'On missed call', desc: 'Bot follows up via SMS when a call goes unanswered' },
            { k: 'onInboundReply', label: 'On inbound reply', desc: 'Bot answers when a lead texts back' },
            { k: 'onWarmDisposition', label: 'On Warm disposition', desc: 'Bot nurtures warm leads without burning them out' },
          ].map((t) => (
            <label key={t.k} className="flex items-start gap-3 panel p-3 cursor-pointer">
              <input type="checkbox" className="mt-1" checked={bot.triggers[t.k]} onChange={(e) => setBot({ ...bot, triggers: { ...bot.triggers, [t.k]: e.target.checked } })} />
              <div>
                <div className="font-semibold text-sm">{t.label}</div>
                <div className="text-xs text-white/60">{t.desc}</div>
              </div>
            </label>
          ))}
        </div>
      )}

      {tab === 'train' && (
        <div className="space-y-3">
          <div className="panel p-4 text-sm text-white/70 flex items-start gap-3">
            <GraduationCap className="w-5 h-5 text-civix-purple shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-white">Train this bot</div>
              <div className="text-xs text-white/50 mt-0.5">Upload winning calls, scripts, and rebuttals. Materials are shared across all your AI bots and the Roleplay engine.</div>
            </div>
          </div>
          <BotTrainer />
        </div>
      )}

      {tab === 'test' && (
        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          <div className="panel p-4 flex flex-col h-[600px]">
            <div className="font-semibold mb-3 flex items-center gap-2"><Bot className="w-5 h-5 text-civix-purple" /> Test conversation</div>
            <div className="flex-1 overflow-y-auto space-y-2 mb-3">
              {transcript.length === 0 && <div className="text-center text-white/40 text-sm mt-12">Send a message to start testing your bot.</div>}
              {transcript.map((t, i) => (
                <div key={i} className={`flex ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${t.role === 'user' ? 'bg-civix-purple/20 border border-civix-purple/40' : 'bg-civix-panelLight border border-civix-border'}`}>
                    <div className="text-[10px] text-white/40 mb-0.5">{t.role === 'user' ? 'Lead' : bot.persona.name}</div>
                    {t.content}
                  </div>
                </div>
              ))}
              {testing && <div className="text-xs text-white/40">{bot.persona.name} is typing…</div>}
            </div>
            <div className="flex gap-2">
              <input className="input flex-1" value={testMsg} onChange={(e) => setTestMsg(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && runTest()} placeholder="Type as if you were the lead…" />
              <button onClick={runTest} disabled={testing} className="btn-primary"><Send className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="panel p-4 text-xs">
            <div className="font-semibold text-sm mb-2">Active config</div>
            <div className="space-y-1 text-white/60">
              <div>Tone: <span className="text-white">{bot.persona.tone}</span></div>
              <div>Voice: <span className="text-white">{bot.persona.voiceId}</span></div>
              <div>Goals: <span className="text-white">{bot.goals.length}</span></div>
              <div>Knowledge: <span className="text-white">{bot.knowledge.length}</span></div>
              <div>Stop kw: <span className="text-white">{bot.guardrails.stopKeywords.length}</span></div>
            </div>
            <div className="mt-3 pt-3 border-t border-civix-border text-white/40">
              In demo mode, replies are synthesized from your config. Connect OpenAI in production for real LLM completions.
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function updateGoal(i, patch) {
    const goals = [...bot.goals]; goals[i] = { ...goals[i], ...patch }; setBot({ ...bot, goals });
  }
  function updateKnowledge(i, patch) {
    const k = [...bot.knowledge]; k[i] = { ...k[i], ...patch }; setBot({ ...bot, knowledge: k });
  }
}

function KeywordList({ label, value, onChange }) {
  const [input, setInput] = useState('');
  return (
    <div>
      <label className="label">{label}</label>
      <div className="flex flex-wrap gap-1 mb-2">
        {value.map((k, i) => (
          <span key={i} className="text-xs px-2 py-1 rounded-sm bg-civix-panelLight border border-civix-border flex items-center gap-1">
            {k}
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-400">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input className="input flex-1" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && input.trim()) { onChange([...value, input.trim()]); setInput(''); } }} placeholder="Add keyword + Enter" />
      </div>
    </div>
  );
}

function synthesizeDemoReply(bot, msg) {
  const m = msg.toLowerCase();
  const name = bot.persona.name || 'Civi';
  if (bot.guardrails.stopKeywords.some((k) => m.includes(k))) {
    return "Got it — you're unsubscribed. We won't message you again. If you change your mind, just text START.";
  }
  if (bot.guardrails.handoffKeywords.some((k) => m.includes(k))) {
    return `Absolutely — I'll have a human agent reach out to you shortly. Thanks for chatting with me!`;
  }
  if (m.includes('price') || m.includes('cost') || m.includes('how much')) {
    return `Great question — pricing depends on a few details (age, state, coverage amount). Let me get you on a quick 15-min call with our agent who can pull exact numbers. What time works for you today?`;
  }
  if (m.includes('what') && (m.includes('this') || m.includes('about'))) {
    return bot.openingMessage?.replace('#first_name', 'there') || `Hi! I'm ${name}, an AI assistant helping our team follow up on insurance quote requests. Want me to set up a quick call to go over your options?`;
  }
  if (m.includes('yes') || m.includes('sure') || m.includes('ok')) {
    return `Awesome. I have openings today at 2 PM, 4 PM, or tomorrow at 10 AM. Which works best?`;
  }
  if (m.includes('no') || m.includes('not interested')) {
    return `Totally understand. If anything changes, just text us. Have a great day!`;
  }
  // Default — apply tone
  const tone = bot.persona.tone;
  if (tone === 'urgent') return `Quick question — are you still looking at coverage this week? I can lock in a slot for you.`;
  if (tone === 'empathetic') return `I hear you. Whatever you decide, no pressure on this end. Want me to share a couple options for you to look over?`;
  return `Thanks for getting back to me. Quick question — are you mainly looking at final expense coverage, or something broader?`;
}
